import { ComponentProps, forwardRef } from "react";
import { useTranslation } from "@vixi/localization";
import TextareaAutosize, { TextareaAutosizeProps } from "react-textarea-autosize";
import { textInputVariants } from "./styling";
import { VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Constants
const default_minRows = 3;
const default_maxRows = 5;

interface TextAreaProps extends TextareaAutosizeProps, VariantProps<typeof textInputVariants> {
  maxLength?: number;
  showMaxLength?: boolean;
  helperText?: (string | undefined | false)[];
}

const TextareaInput = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, state, showMaxLength, ...props }, ref) => (
    <TextareaAutosize
      className={cn(
        textInputVariants({ state }),
        "peer/input block resize-none px-3.5 py-2.5",
        {
          "pb-[2em]": showMaxLength,
        },
        className,
      )}
      data-state={state}
      {...props}
      ref={ref}
    />
  ),
);

const TextAreaCharacterNote = forwardRef<HTMLSpanElement, ComponentProps<"span">>(
  ({ ...props }, ref) => (
    <span ref={ref} {...props} className="fs-14 absolute end-3 bottom-1.25 font-light" />
  ),
);

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ minRows, maxRows, rows, helperText, state, ...props }, ref) => {
    const { t, tNum } = useTranslation();

    console.log("HELPER TEXT:", helperText);

    return (
      <div className="relative block">
        <div className="peer relative" data-state={state}>
          <TextareaInput
            minRows={minRows ?? rows ?? default_minRows}
            maxRows={maxRows ?? rows ?? default_maxRows}
            state={state}
            ref={ref}
            {...props}
          />
          {props.maxLength && props.showMaxLength && (
            <TextAreaCharacterNote>
              {tNum(props.maxLength)} {t("Input.CharactersMax")}
            </TextAreaCharacterNote>
          )}
        </div>
        {helperText?.length && (
          <div className={cn("peer-data-[state=error]:text-destructive space-y-1", "")}>
            {helperText?.map((text) => text && <p className="text-sm text-current">{text}</p>)}
          </div>
        )}
      </div>
    );
  },
);

export const TransparentTextArea = forwardRef<HTMLTextAreaElement, TextareaAutosizeProps>(
  (props, ref) => (
    <TextareaAutosize
      {...props}
      ref={ref}
      className={cn(
        "min-h-0 w-full resize-none border-0 bg-transparent font-semibold text-black focus:outline-none",
        props.className,
      )}
    />
  ),
);
